import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, KanbanSquare, Briefcase, Users, BarChart3, Sparkles, Settings, LogOut, Search, Bell, ChevronDown, ShieldCheck, Wallet, Clock, GraduationCap, Building2, FileClock, Inbox } from "lucide-react";
import { APP, NAV } from "@/constants/testIds";
import AICopilotPanel from "@/components/AICopilotPanel";

const NAV_BY_ROLE = {
  admin: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/app/dashboard" },
    { key: "ats", label: "ATS Pipeline", icon: KanbanSquare, to: "/app/ats" },
    { key: "jobs", label: "Jobs", icon: Briefcase, to: "/app/jobs" },
    { key: "candidates", label: "Candidates", icon: Users, to: "/app/candidates" },
    { key: "leads", label: "Leads", icon: Inbox, to: "/app/leads" },
    { key: "analytics", label: "Analytics", icon: BarChart3, to: "/app/analytics" },
    { key: "ai", label: "AI Copilot", icon: Sparkles, to: "/app/ai" },
    { key: "payroll", label: "Payroll", icon: Wallet, to: "/app/payroll" },
    { key: "timesheets", label: "Timesheets", icon: Clock, to: "/app/timesheets" },
    { key: "vendors", label: "Vendors", icon: Building2, to: "/app/vendors" },
    { key: "learning", label: "Learning", icon: GraduationCap, to: "/app/learning" },
    { key: "audit", label: "Audit Log", icon: FileClock, to: "/app/audit" },
    { key: "settings", label: "Settings", icon: Settings, to: "/app/settings" },
  ],
  recruiter: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/app/dashboard" },
    { key: "ats", label: "ATS Pipeline", icon: KanbanSquare, to: "/app/ats" },
    { key: "jobs", label: "Jobs", icon: Briefcase, to: "/app/jobs" },
    { key: "candidates", label: "Candidates", icon: Users, to: "/app/candidates" },
    { key: "leads", label: "Leads", icon: Inbox, to: "/app/leads" },
    { key: "analytics", label: "Analytics", icon: BarChart3, to: "/app/analytics" },
    { key: "ai", label: "AI Copilot", icon: Sparkles, to: "/app/ai" },
    { key: "timesheets", label: "Timesheets", icon: Clock, to: "/app/timesheets" },
    { key: "vendors", label: "Vendors", icon: Building2, to: "/app/vendors" },
    { key: "learning", label: "Learning", icon: GraduationCap, to: "/app/learning" },
    { key: "settings", label: "Settings", icon: Settings, to: "/app/settings" },
  ],
  client: [
    { key: "client", label: "My Requisitions", icon: LayoutDashboard, to: "/app/client" },
    { key: "jobs", label: "All Jobs", icon: Briefcase, to: "/app/jobs" },
    { key: "analytics", label: "Reports", icon: BarChart3, to: "/app/analytics" },
    { key: "settings", label: "Settings", icon: Settings, to: "/app/settings" },
  ],
  candidate: [
    { key: "candidate", label: "My Journey", icon: LayoutDashboard, to: "/app/candidate" },
    { key: "jobs", label: "Browse Jobs", icon: Briefcase, to: "/app/jobs" },
    { key: "employee", label: "My Growth", icon: GraduationCap, to: "/app/employee" },
    { key: "learning", label: "Learning", icon: GraduationCap, to: "/app/learning" },
    { key: "settings", label: "Profile", icon: Settings, to: "/app/settings" },
  ],
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const items = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.recruiter;

  const doLogout = () => { logout(); nav("/"); };

  return (
    <div className="flex h-screen bg-[#F4F4F5]/40">
      {/* Sidebar */}
      <aside data-testid={APP.sidebar} className="w-64 border-r border-border bg-background flex-shrink-0 flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <NavLink to="/" data-testid={NAV.brandLink} className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="" className="h-8 w-8" />
            <span className="font-display font-bold tracking-tight text-sm">TechAI Fortune</span>
          </NavLink>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground px-3 py-2">Workspace</div>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={APP.sidebarLink(item.key)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive ? "bg-[#0047AB] text-white font-medium" : "text-foreground hover:bg-[#F4F4F5]"
                  }`
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#FAFAFA]">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <div className="text-xs">
              <div className="font-semibold">SOC2 Ready</div>
              <div className="text-muted-foreground">Audit log active</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header data-testid={APP.topbar} className="h-16 border-b border-border bg-background/70 backdrop-blur-xl px-6 flex items-center gap-4">
          <div className="flex-1 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input placeholder="Search candidates, jobs, clients…" className="w-full h-9 pl-9 pr-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]" />
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid={APP.aiCopilotToggle}
            onClick={() => setCopilotOpen((v) => !v)}
            className="gap-2 border-indigo-200 hover:bg-indigo-50 text-indigo-700"
          >
            <Sparkles className="h-4 w-4" />
            AI Copilot
          </Button>
          <button className="relative h-9 w-9 grid place-items-center rounded-md hover:bg-[#F4F4F5]">
            <Bell className="h-4 w-4" />
            <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-[#DC2626]" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-[#F4F4F5]">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#0047AB] to-[#4338CA] text-white grid place-items-center font-bold text-xs">
                  {user?.name?.[0]}
                </div>
                <div className="text-left hidden md:block">
                  <div className="text-xs font-semibold">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
                </div>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/app/settings")}>Settings</DropdownMenuItem>
              <DropdownMenuItem data-testid={APP.logoutBtn} onClick={doLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <AICopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
}
